<template>
	<main class="app-main">
		<div class="content-wrap">
			<section class="content-section" v-if="investmentsIadAccount">
				<header class="section-header">
					<h2 class="section-title">{{ $t('routes.investments_iad_account.title') }}:
						<span v-if="investmentsIadAccount.name">{{ investmentsIadAccount.name }} - </span>
						{{ investmentsIadAccount.funds[0].NazovFondu }}
					</h2>
				</header>
				<article>
					<actions-panel>
						<a class="button style-1" @click.prevent="$router.safeBack">
							<span class="inner">{{ $t('general.back') }}</span>
						</a>
					</actions-panel>
					<chart-panels-group :panels-data="panelsData" :charts-data="chartsData" v-if="funds.length" :tabs="true"/>
					<p v-else>{{ $t('investments.no_data') }}</p>
				</article>
			</section>
		</div>
	</main>
</template>

<script>
import investmentsMixin from "@/mixins/investmentsMixin";
import ActionsPanel from "@/components/panels/ActionsPanel";
import ChartPanelsGroup from "@/components/containers/ChartPanelsGroup";
import PanelBoxesContainer from "@/components/containers/PanelBoxesContainer";
import funds from '@/data/funds';

export default {
	name: "InvestmentsIadAccount",
	components: {
		ActionsPanel,
		ChartPanelsGroup,
		PanelBoxesContainer
	},
	mixins: [investmentsMixin],
	computed: {
		investmentsIadAccount() {
			return this.$store.state.views.investmentsIadAccount;
		},
		funds() {
			let data = [];
			this.investmentsIadAccount.funds.forEach(item => {
				data.push(item);
			});

			return data;
		},
		chartsData() {
			let data = [
				this.balanceChart,
				this.portfoliosChart,
			];

			return data;
		},
		balanceChart() {
			let data = this.getBalanceChartOptions();
			data.tabName = this.$t('investments.profit');

			let series = [
				{
					name: this.$t('investments.account_balance'),
					data: [],
				},
				{
					name: this.$t('investments.transactions'),
					data: [],
				}
			];

			let deposit = 0;
			let funds = {};
			let totalBalance = 0;

			this.transactions.forEach(item => {
				if (!funds[item.ISIN]) {
					funds[item.ISIN] = {
						isin: item.ISIN,
						totalAmount: 0,
						lastRate: 0,
					};
				}

				funds[item.ISIN].totalAmount += item.ObjemOperacievPL;
				funds[item.ISIN].lastRate = item.CenaZaKus;

				let itemBalance = item.ObjemOperacieVmene;
				if (item.ObjemOperacievPL < 0) {
					itemBalance = itemBalance * -1;
				}

				let currentValue = 0;
				for (let fund in funds) {
					currentValue += funds[fund].totalAmount * funds[fund].lastRate;
				}

				totalBalance = currentValue;
				deposit += itemBalance;
				if (deposit < 0){
					deposit = 0;
				}

				series[0].data.push({
					x: item.Datum,
					y: totalBalance.toFixed(2),
				});

				series[1].data.push({
					x: item.Datum,
					y: deposit.toFixed(2),
					amount: itemBalance,
					data: item,
				});

			});

			let currentDate = new Date().toISOString().slice(0, 10);
			series[0].data.push({
				x: currentDate,
				y: this.investmentsIadAccount.balance,
			});

			series[1].data.push({
				x: currentDate,
				y: deposit.toFixed(2),
			});

			data.series = series;

			data.totalDeposits = this.investmentsIadAccount.deposit;
			data.totalWithdrawals = this.investmentsIadAccount.withdrawal;
			data.totalFees = this.investmentsIadAccount.fee;
			data.totalBalance = this.investmentsIadAccount.balance;
			data.totalReturn = this.investmentsIadAccount.return;

			return data;
		},
		portfoliosChart() {
			let data = this.getPortfoliosChartOptions();
			data.tabName = this.$t('investments.allocation');

			let series = [];
			let labels = [];

			this.funds.forEach(item => {
				series.push(item.ObejmInvesticieEur);

				let name = item.NazovFondu;
				let isin = item.ISIN.trim();
				if (item.NazovFondu.trim() === isin) {
					name = funds[isin];
				}
				labels.push(name);
			});

			data.chartOptions.labels = labels;
			data.series = series;

			return data;
		},
		panelsData() {
			let data = [];

			this.investmentsIadAccount.funds.forEach(item => {
				let currency = this.getCurrencySymbol(item.Mena);

				let totalBalance = 0;
				let transactions = this.transactions.filter((transaction) => {
					return item.ISIN === transaction.ISIN;
				});

				transactions.forEach(transaction => {
					switch (transaction.TypOperacie) {
						case 'k':
						case 'z':
						case 'sw':
							totalBalance += transaction.ObjemOperacieVmene;
							break;
						case 'p':
							totalBalance -= transaction.ObjemOperacieVmene;
							break;
					}
				});

				let returnPercentage = (item.ObejmInvesticie / totalBalance) * 100 - 100;

				let name = item.NazovFondu;
				let isin = item.ISIN.trim();
				if (item.NazovFondu.trim() === isin) {
					name = funds[isin];
				}

				data.push({
					id: item.id,
					category: this.$t('investments.fund'),
					name: name,
					description: this.$t('investments.fund_balance'),
					price: item.ObejmInvesticie,
					returnPercentage: returnPercentage,
					type: 'portfolio',
					portfolioType: 'iad',
					isin: isin,
					currency: currency,
					detail: !!item.ISIN,
				});
			});

			return data;
		},
		transactions() {
			if (!this.investmentsIadAccount) {
				return [];
			}

			let transactions = this.investmentsIadAccount.transactions;

			let newTransactions = [];
			let usedDates = {};

			transactions.forEach(item => {
				if(usedDates[item.Datum] === undefined){
					usedDates[item.Datum] = 0;
				} else {
					usedDates[item.Datum]++;
					let date = item.Datum.split('T');
					
					let time = new Date(0, 0, 0, 0);
					let secondsToAdd = usedDates[item.Datum];

					time.setSeconds(time.getSeconds() + secondsToAdd);

					let hours = time.getHours().toString().padStart(2, '0');
					let minutes = time.getMinutes().toString().padStart(2, '0');
					let seconds = time.getSeconds().toString().padStart(2, '0');
					
					let newTime = `${hours}:${minutes}:${seconds}`;

					date[1] = newTime;
					item.Datum = date.join('T');
				}

				newTransactions.push(item);
			});

			transactions = newTransactions.sort((a, b) => {
				return new Date(a.Datum) - new Date(b.Datum);
			});

			return transactions;
		},
	}
}
</script>

<style scoped>

</style>